export type Environment = 'local' | 'dev' | 'prod';
export type Locale =
  | 'bg-BG'
  | 'cs-CZ'
  | 'da-DK'
  | 'de-AT'
  | 'de-CH'
  | 'de-DE'
  | 'el-GR'
  | 'en'
  | 'en-GB'
  | 'en-IE'
  | 'es-ES'
  | 'fi-FI'
  | 'fr-BE'
  | 'fr-CH'
  | 'fr-FR'
  | 'hr-HR'
  | 'hu-HU'
  | 'it-IT'
  | 'nl-NL'
  | 'nb-NO'
  | 'pl-PL'
  | 'pt-PT'
  | 'ro-RO'
  | 'ru-RU'
  | 'sk-SK'
  | 'sl-SI'
  | 'sv-SE'
  | 'zu';

export type ShopBundle = 'zooplus' | 'bitiba';

export function isShopBundle(value: unknown): value is ShopBundle {
  return value === 'zooplus' || value === 'bitiba';
}

export type ContainerType = 'registration' | 'myaccount' | 'default';

export type CustomerType = 'GUEST_CUSTOMER' | 'EXISTING_CUSTOMER' | 'NEW_CUSTOMER';

export function isCustomerType(value: unknown): value is CustomerType {
  return value === 'GUEST_CUSTOMER' || value === 'EXISTING_CUSTOMER' || value === 'NEW_CUSTOMER';
}

export const supportedPaymentMethods = {
  AK: 'AK', // ADYEN_CREDIT_CARD
  BK: 'BK', // BRAINTREE_CREDIT_CARD
  R: 'R', // BILL
  K: 'K', // CREDIT_CARD
  D: 'D', // ZPP
  B: 'B', // DIRECT_DEBIT
  BS: 'BS', // DIRECT_DEBIT_SEPA
  VK: 'VK', // PREPAYMENT
  PP: 'PP', // PAYPAL
  ID: 'ID', // IDEAL
  CD: 'CD', // CASH_ON_DELIVERY
  DP: 'DP', // DOT_PAY,
  BANCONTACT_MOBILE: 'BANCONTACT_MOBILE',
};

export type PaymentMethod = keyof typeof supportedPaymentMethods;

export function isPaymentMethod(value: unknown): value is PaymentMethod {
  return (
    typeof value === 'string' &&
    typeof (supportedPaymentMethods as Record<string, unknown>)[value] !== 'undefined'
  );
}

export type RetryPaymentMethod =
  | 'BANCONTACT'
  | 'BANCONTACT_MOBILE'
  | 'CREDIT_CARD'
  | 'DIRECT_DEBIT_SEPA'
  | 'DOTPAY'
  | 'ONLINE_BANKING_PL'
  | 'IDEAL'
  | 'INVOICE'
  | 'MULTIBANCO'
  | 'PAYPAL'
  | 'PAYTRAIL'
  | 'PREPAYMENT'
  | 'TRUSTLY'
  | 'TWINT';

// Lower case values of the ZPP-API enum: https://src.private.zooplus.net/projects/ZPP/repos/zpp-api/browse/src/main/java/com/zooplus/api/message/paymentmethods/creditcards/CreditCardBrand.java
// Which are also the values known by shop-checkout: https://src.private.zooplus.net/projects/CHK/repos/shop-checkout/browse/src/main/java/com/zooplus/dto/payment/creditcards/CreditCardBrand.java
export type CreditCardBrand =
  | 'american_express'
  | 'carte_bancaire'
  | 'diners_club'
  | 'maestro'
  | 'master_card'
  | 'other'
  | 'visa';

export const cardTypeOfCreditCardBrand: Record<CreditCardBrand, Nullable<CardType>> = {
  american_express: 'american-express',
  carte_bancaire: 'cartebancaire',
  diners_club: 'diners-club',
  maestro: 'maestro',
  master_card: 'mastercard',
  other: null,
  visa: 'visa',
};

// beware: these values are actually a weird mix of supported cards and non-supported cards (elo, mir, etc) and also non-matching values (different spelling and case) for both Adyen and Braintree
export const supportedCardTypes = {
  visa: 'visa',
  mastercard: 'mastercard',
  'american-express': 'american-express',
  'diners-club': 'diners-club',
  discover: 'discover',
  jcb: 'jcb',
  unionpay: 'unionpay',
  maestro: 'maestro',
  elo: 'elo',
  mir: 'mir',
  hiper: 'hiper',
  hipercard: 'hipercard',
  cartebancaire: 'cartebancaire',
};

export type CardType = keyof typeof supportedCardTypes;

export type RetryPaymentStatus =
  | 'SUCCESSFUL'
  | 'UNSUCCESSFUL'
  | 'CANCELLED'
  | 'PROCESSING'
  | 'UNKNOWN'
  | 'SERVICE_NOT_AVAILABLE';

export type PspConnector = 'ADYEN' | 'BRAINTREE';

export type Maybe<T> = T | undefined;

export type Nullable<T> = T | null;

export type MaybeNullable<T> = T | undefined | null;

export type Additional3dsInfo = {
  email: string;
  shippingAddress: Address;
  billingAddress: Address;
  accountCreateDate: number; // timestamp
  creditCardCreateDate?: number; // timestamp
  creditCardHolderName?: string;
  accountId: number;
  ipAddress: string;
  articleIds: number[];
  userAgent: string;
};

export type Address = {
  firstName: string;
  lastName: string;
  street: string;
  company: string | null;
  city: string;
  isoCountry: string;
  country: string;
  postalCode: string;
  phoneNumber: string | null;
};

export enum BraintreeShippingNameIndicator {
  ACCOUNT_AND_SHIPPING_NAME_IDENTICAL_01 = '01',
  ACCOUNT_AND_SHIPPING_NAME_DIFFERENT_02 = '02',
}

export enum BraintreeAddressMatch {
  BILLING_AND_SHIPPING_ADDRESS_IDENTICAL_01 = 'Y',
  BILLING_AND_SHIPPING_ADDRESS_DIFFERENT_02 = 'N',
}
